import { Box, Table, Thead, Tbody, Tr, Th, Td, Button, Text, IconButton, HStack, Select, VStack, Input, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { DownloadIcon, ChevronDownIcon } from "@chakra-ui/icons";
import React, { useRef } from "react";
import useDownloadHook from "../hooks/DownloadHook";


const DownloadAmbulanceCoverage: React.FC = () => {
    const { 
        documentList, selectedCompany, setSelectedCompany, currentPage, limit, 
        handlePageChange, handleLimitChange, handleDownload, parseFileName, handleInitiateDownload,
        searchTerm, setSearchTerm, isOpen, setIsOpen, filteredCompanies, selectedCompanyName,
        isGenerating, downloadingFiles
    } = useDownloadHook();
    
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <Box p={4}>
            <VStack spacing={6} align="stretch">
                <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={4} textAlign={"center"}>Generate New Report</Text>
                    <HStack spacing={4} justifyContent={"center"}>
                        <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
                            <MenuButton
                                as={Button}
                                rightIcon={<ChevronDownIcon />}
                                onClick={() => setIsOpen(!isOpen)}
                                width="300px"
                                textAlign="left"
                            >
                                {selectedCompanyName || "Select a company"}
                            </MenuButton>
                            <MenuList maxH="300px" overflowY="auto">
                                <Box p={2} onClick={(e) => e.stopPropagation()}>
                                    <Input
                                        ref={inputRef}
                                        placeholder="Search companies..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setTimeout(() => inputRef.current?.focus(), 0);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                                e.preventDefault();
                                            }
                                        }}
                                        onFocus={(e) => e.stopPropagation()}
                                        size="sm"
                                        autoComplete="off"
                                    />
                                </Box>
                                {filteredCompanies.map((company) => (
                                    <MenuItem
                                        key={company._id}
                                        onClick={() => {
                                            setSelectedCompany(company._id);
                                            setSearchTerm("");
                                            setIsOpen(false);
                                        }}
                                    >
                                        {company.companyName}
                                    </MenuItem>
                                ))}
                                {filteredCompanies.length === 0 && (
                                    <MenuItem isDisabled>
                                        No companies found
                                    </MenuItem>
                                )}
                            </MenuList>
                        </Menu>
                        <Button 
                            colorScheme="green" 
                            onClick={handleInitiateDownload}
                            isLoading={isGenerating}
                            loadingText="Generating..."
                            disabled={isGenerating || !selectedCompany}
                        >
                            Generate Report
                        </Button>
                    </HStack>
                </Box>
                
                <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={4}>Available Reports</Text>
                    
                    {documentList?.pagination && (
                        <HStack justifyContent="space-between" mb={4}>
                            <HStack>
                                
                            </HStack>
                            
                            <HStack>
                                <Select
                                    value={limit}
                                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                                    width="80px"
                                    size="sm"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </Select>
                                <Button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={!documentList.pagination.hasPreviousPage}
                                    colorScheme={documentList.pagination.hasPreviousPage ? "green" : "gray"}
                                    size="sm"
                                >
                                    Previous
                                </Button>
                                <Text fontSize="sm">
                                    Page {documentList.pagination.currentPage} of {documentList.pagination.totalPages}
                                </Text>
                                <Button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={!documentList.pagination.hasNextPage}
                                    colorScheme={documentList.pagination.hasNextPage ? "green" : "gray"}
                                    size="sm"
                                >
                                    Next
                                </Button>
                                <Select
                                    placeholder="Select Page"
                                    value={currentPage}
                                    onChange={(e) => handlePageChange(Number(e.target.value))}
                                    width="120px"
                                    size="sm"
                                >
                                    {Array.from({ length: documentList.pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                                        <option key={pageNum} value={pageNum}>
                                            Page {pageNum}
                                        </option>
                                    ))}
                                </Select>
                            </HStack>
                        </HStack>
                    )}
            
            {documentList?.files?.length > 0 ? (
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            <Th>Name</Th>
                            <Th>Company Name</Th>
                            <Th>Generated Time</Th>
                            <Th>File Size</Th>
                            <Th>Download</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {documentList.files.map((file: any, index: number) => {
                            const { name, companyId, timestamp } = parseFileName(file.fileName);
                            return (
                                <Tr key={index}>
                                    <Td>{name}</Td>
                                    <Td>{companyId}</Td>
                                    <Td>{timestamp}</Td>
                                    <Td>{(file.size / 1024).toFixed(2)} KB</Td>
                                    <Td>
                                        <IconButton
                                            aria-label="Download file"
                                            icon={<DownloadIcon />}
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={() => handleDownload(file.fileName)}
                                            isLoading={downloadingFiles.has(file.fileName)}
                                            disabled={downloadingFiles.has(file.fileName)}
                                        />
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                    ) : (
                        <Text>No reports available</Text>
                    )}
                </Box>
            </VStack>
        </Box>
    );
}

export default DownloadAmbulanceCoverage;